import { useRef, useCallback, useEffect } from 'react';
import type { ChatMessage } from '../../ChatPanel';

/**
 * Hook para gestionar el streaming de mensajes
 *
 * Responsabilidades:
 * - Mantener refs de control de streaming (AbortController, messageId)
 * - Limpiar/finalizar mensajes de streaming
 * - Manejar cancelación de streaming
 * - Absolute timeout safety net for hung streams
 *
 * @param callbacks - Funciones callback para actualizar estado
 * @returns Estado y funciones para gestionar streaming
 */

// Safety net: if a stream hangs silently (no tokens, no error), force-abort after 5 min
const ABSOLUTE_STREAMING_TIMEOUT_MS = 300_000;

interface UseStreamingManagerProps {
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  stopTyping: () => void;
  stopLoading: () => void;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
}

export function useStreamingManager({
  onMessagesUpdate,
  stopTyping,
  stopLoading,
  setError,
  setSuccessMessage,
}: UseStreamingManagerProps) {
  // Refs para control de streaming
  const streamAbortControllerRef = useRef<AbortController | null>(null);
  const streamingMessageIdRef = useRef<string | null>(null);
  const cancellationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const absoluteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the absolute streaming timeout
  const clearAbsoluteTimeout = useCallback(() => {
    if (absoluteTimeoutRef.current) {
      clearTimeout(absoluteTimeoutRef.current);
      absoluteTimeoutRef.current = null;
    }
  }, []);

  // Helper para limpiar refs de streaming (evita código duplicado)
  const cleanupStreamingRefs = useCallback(() => {
    streamAbortControllerRef.current = null;
    streamingMessageIdRef.current = null;
    clearAbsoluteTimeout();
  }, [clearAbsoluteTimeout]);

  // Helper para limpiar mensajes de streaming de la UI (elimina completamente)
  const clearStreamingMessages = useCallback(() => {
    onMessagesUpdate((prev) => {
      return prev.filter((msg) => {
        const id = msg.id ?? '';
        const isStreamId = id.startsWith('streaming-') || id.startsWith('batch-');
        return !(msg.isStreaming || isStreamId);
      });
    });
  }, [onMessagesUpdate]);

  // Helper para finalizar mensajes de streaming sin eliminarlos (preserva contenido)
  // C1: Optionally mark messages as cancelled
  const finalizeStreamingMessages = useCallback(
    (markCancelled: boolean = false) => {
      onMessagesUpdate((prev) => {
        return prev.map((msg) => {
          const id = msg.id ?? '';
          const isStreamId = id.startsWith('streaming-') || id.startsWith('batch-');
          // Si es un mensaje en streaming, marcarlo como completado pero mantener su contenido
          if (msg.isStreaming || isStreamId) {
            return {
              ...msg,
              isStreaming: false,
              ...(markCancelled && msg.text?.trim() ? { cancelled: true } : {}),
              // Mantener todo el contenido que ya se ha renderizado
            };
          }
          return msg;
        });
      });
    },
    [onMessagesUpdate]
  );

  // Helper para manejar la cancelación del streaming (centraliza lógica de cancelación)
  // Nota: setSuccessMessage es setSuccessMessageWithNotification desde ChatInputArea,
  // que notifica al padre (MainLayout) para mostrar el mensaje en el modal de ChatPanel
  const handleStreamCancellation = useCallback(() => {
    cleanupStreamingRefs();
    // C1: En lugar de eliminar, finalizar el mensaje preservando su contenido y marcarlo como cancelado
    finalizeStreamingMessages(true);
    setError(null);
    // Establecer mensaje de éxito (se mostrará en el modal de ChatPanel, no en MessageStatusBar)
    setSuccessMessage('Consulta cancelada');
    // Cancel previous timeout before starting a new one
    if (cancellationTimeoutRef.current) {
      clearTimeout(cancellationTimeoutRef.current);
    }
    const CANCELLATION_MESSAGE_DURATION_MS = 3000;
    cancellationTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      cancellationTimeoutRef.current = null;
    }, CANCELLATION_MESSAGE_DURATION_MS);
    stopTyping();
    stopLoading();
  }, [
    cleanupStreamingRefs,
    finalizeStreamingMessages,
    stopTyping,
    stopLoading,
    setError,
    setSuccessMessage,
  ]);

  // Start absolute timeout: safety net for streams that hang silently
  const startAbsoluteTimeout = useCallback(() => {
    clearAbsoluteTimeout();
    absoluteTimeoutRef.current = setTimeout(() => {
      streamAbortControllerRef.current?.abort();
      cleanupStreamingRefs();
      finalizeStreamingMessages(true);
      stopTyping();
      stopLoading();
      setError('La respuesta tardó demasiado. Intenta de nuevo.');
    }, ABSOLUTE_STREAMING_TIMEOUT_MS);
  }, [
    clearAbsoluteTimeout,
    cleanupStreamingRefs,
    finalizeStreamingMessages,
    stopTyping,
    stopLoading,
    setError,
  ]);

  // Cleanup on unmount: abort active stream + clear all timers
  useEffect(() => {
    return () => {
      if (cancellationTimeoutRef.current) {
        clearTimeout(cancellationTimeoutRef.current);
      }
      if (absoluteTimeoutRef.current) {
        clearTimeout(absoluteTimeoutRef.current);
      }
      // Abort any active stream to prevent state updates on unmounted component
      streamAbortControllerRef.current?.abort();
    };
  }, []);

  // Determinar si hay streaming activo
  const isStreaming = streamAbortControllerRef.current !== null;

  return {
    // Refs expuestos
    streamAbortControllerRef,
    streamingMessageIdRef,

    // Funciones de gestión
    cleanupStreamingRefs,
    clearStreamingMessages,
    finalizeStreamingMessages,
    handleStreamCancellation,
    startAbsoluteTimeout,
    clearAbsoluteTimeout,

    // Estado computado
    isStreaming,
  };
}
